import { useEffect, useRef } from 'react';
import { five9CallsService } from 'src/services/five9CallsService';
import { createFive9CallQueue } from 'src/utils/five9/five9CallQueue';
import { mapFive9CallEvent } from 'src/utils/five9/mapFive9CallEvent';
import { subscribeToFive9CallLifecycleEvents } from 'src/utils/five9/five9CallLifecycleEvents';

export const isFive9CallPublisherEnabled = () => (
  process.env.REACT_APP_ENABLE_FIVE9_CALL_PUBLISHER !== 'false'
  // Fail closed when the backend base URL isn't configured: without it,
  // `five9CallsService.execute()`/`get()` would still issue requests, but
  // as RELATIVE paths resolved against the frontend's own origin (wrong-
  // origin traffic) instead of erroring, while `keepalive()` silently
  // no-ops — a misconfigured-but-silently-active state rather than a
  // clean, observable kill switch.
  && Boolean(process.env.REACT_APP_DISPATCH_QORE_API)
);

const sessionIdFor = (call) => call?.five9SessionId || call?.sessionId || null;

const reconciliationFingerprintFor = (call) => JSON.stringify([
  sessionIdFor(call),
  call?.five9InteractionId || call?.interactionId || call?.callSid || null,
  call?.five9CallId || call?.callId || null,
  call?.state || null,
  call?.direction || null,
  call?.lastEventAt || null,
  call?.startedAt || null
]);

export default function useFive9CallPublisher({
  enabled,
  identity,
  calls
}) {
  const queueRef = useRef(null);
  const reconciledSessionIdsRef = useRef(new Map());
  const wasEnabledRef = useRef(false);
  const loadedPendingReconciledRef = useRef(false);
  const publisherEnabled = isFive9CallPublisherEnabled();

  useEffect(() => {
    if (!publisherEnabled || !identity) {
      queueRef.current?.dispose();
      queueRef.current = null;
      reconciledSessionIdsRef.current.clear();
      wasEnabledRef.current = false;
      loadedPendingReconciledRef.current = false;
      return undefined;
    }
    const queue = createFive9CallQueue({
      identity,
      service: five9CallsService,
      enabled: false
    });
    queueRef.current = queue;
    reconciledSessionIdsRef.current.clear();
    wasEnabledRef.current = false;
    loadedPendingReconciledRef.current = false;
    const unsubscribe = subscribeToFive9CallLifecycleEvents(({ type, payload }) => {
      queue.enqueue(mapFive9CallEvent(type, payload));
    });
    const handlePageHide = () => queue.flushKeepalive();
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      unsubscribe();
      queue.dispose();
      queueRef.current = null;
    };
  }, [identity, publisherEnabled]);

  useEffect(() => {
    const queue = queueRef.current;
    if (!publisherEnabled || !queue) return;
    queue.setEnabled(enabled);
    if (!enabled) {
      wasEnabledRef.current = false;
      return;
    }
    const diagnostics = queue.getDiagnostics();
    const gainedOwnership = !wasEnabledRef.current;
    wasEnabledRef.current = true;
    const hasReloadedPendingWork = diagnostics.loadedPendingCount > 0
      && !loadedPendingReconciledRef.current;
    const callsToReconcile = [];
    Object.values(calls || {}).forEach((call) => {
      const sessionId = sessionIdFor(call);
      if (!sessionId) return;
      const fingerprint = reconciliationFingerprintFor(call);
      const previous = reconciledSessionIdsRef.current.get(String(sessionId));
      if (gainedOwnership || hasReloadedPendingWork || previous !== fingerprint) {
        callsToReconcile.push(call);
        reconciledSessionIdsRef.current.set(String(sessionId), fingerprint);
      }
    });
    if (callsToReconcile.length) {
      if (hasReloadedPendingWork) loadedPendingReconciledRef.current = true;
      queue.reconcile(callsToReconcile);
    } else if (hasReloadedPendingWork || (gainedOwnership && diagnostics.pendingCount > 0)) {
      // There is no active call to reconcile, but the durable queue can
      // still contain a lifecycle/transcript update from before this tab
      // stood down. Both reload-pending work and ownership-regain work use
      // this single drain-only path so neither can get stuck waiting for a
      // later CTI event, while unchanged call-object churn remains inert.
      if (hasReloadedPendingWork) loadedPendingReconciledRef.current = true;
      queue.drain();
    }
  }, [enabled, identity, calls, publisherEnabled]);
}
